/**
 * COMPLETE TRAFFIC HANDLER
 * With natural location names and all required exports
 */

import { coordinatesToLocation, getCityName } from './TrafficlocationConverter';

const TRAFFIC_CONFIG = {
  TOMTOM_KEY: process.env.REACT_APP_TOMTOM_KEY || "nF4LIbBHx737BrJdFHLUl0Ns8MxtoYHW",
  HERE_KEY: process.env.REACT_APP_HERE_KEY,
};

/**
 * Extract key waypoints and major roads from route guidance
 */
const extractRouteWaypoints = (route) => {
  try {
    const instructions = route.guidance?.instructions || [];
    const waypoints = [];
    const majorRoads = [];
    
    instructions.forEach((instruction, index) => {
      const message = instruction.message || instruction.street || '';
      const point = instruction.point;
      
      if (message.match(/I-\d+|US-\d+|Route \d+|Highway|Parkway|Boulevard|Avenue/i)) {
        if (!majorRoads.includes(message)) {
          majorRoads.push(message);
        }
      }
      
      if (index % 4 === 0 && point && message) {
        waypoints.push({
          location: `${point.latitude},${point.longitude}`,
          instruction: message
        });
      }
    });
    
    return { waypoints: waypoints.slice(0, 3), majorRoads: majorRoads.slice(0, 2) };
  } catch (error) {
    console.error('Error extracting waypoints:', error);
    return { waypoints: [], majorRoads: [] };
  }
};

/**
 * Get human-readable locations for route waypoints
 */
const getWaypointNames = async (waypoints) => {
  const names = [];
  
  console.log(`🗺️ Converting ${waypoints.length} waypoints to location names...`);
  
  for (let i = 0; i < waypoints.length; i++) {
    const waypoint = waypoints[i];
    
    // ✅ Add delay between waypoint requests (except first one)
    if (i > 0) {
      console.log(`⏳ Waiting 300ms before next waypoint...`);
      await new Promise(resolve => setTimeout(resolve, 300));
    }
    
    try {
      const fullLocation = await coordinatesToLocation(waypoint.location);
      const cityName = getCityName(fullLocation);
      
      // ✅ FIXED: Better validation
      if (cityName && cityName.length > 2) {
        names.push(cityName);
        console.log(`  ✅ Waypoint: ${waypoint.location} → ${cityName}`);
      } else {
        console.log(`  ⚠️ Skipping waypoint (could not extract city name from: ${fullLocation})`);
      }
      
    } catch (error) {
      console.error(`  ❌ Error converting waypoint:`, error.message);
      continue;
    }
  }
  
  console.log(`✅ Got ${names.length} valid location names`);
  return names;
};

/**
 * Enhanced TomTom Traffic API with natural location names
 */
const getTomTomTrafficWithAlternatives = async (origin, destination) => {
  try {
    console.log('🚗 Fetching traffic from TomTom with smart alternatives...');
    console.log('   Origin:', origin);
    console.log('   Destination:', destination);
    
    // ✅ VALIDATE COORDINATES FORMAT
    const validateCoords = (coords, label) => {
      if (!coords || typeof coords !== 'string') {
        throw new Error(`${label} is invalid: ${coords}`);
      }
      
      const parts = coords.split(',');
      if (parts.length !== 2) {
        throw new Error(`${label} must be in format "lat,lng": ${coords}`);
      }
      
      const [lat, lng] = parts.map(s => parseFloat(s.trim()));
      if (isNaN(lat) || isNaN(lng)) {
        throw new Error(`${label} contains non-numeric values: ${coords}`);
      }
      
      if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
        throw new Error(`${label} out of valid range: ${coords}`);
      }
      
      return true;
    };
    
    // Validate both coordinates
    validateCoords(origin, 'Origin');
    validateCoords(destination, 'Destination');
    
    const url = `https://api.tomtom.com/routing/1/calculateRoute/${origin}:${destination}/json?key=${TRAFFIC_CONFIG.TOMTOM_KEY}&traffic=true&travelMode=car&maxAlternatives=2&alternativeType=anyRoute&routeType=fastest&instructionsType=text&language=en-US`;
    
    console.log('   API URL (first 120 chars):', url.substring(0, 120) + '...');
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);
    
    const response = await fetch(url, { signal: controller.signal });
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      // ✅ GET DETAILED ERROR MESSAGE
      const errorBody = await response.text().catch(() => 'Could not read error body');
      console.error('❌ TomTom API Response:', {
        status: response.status,
        statusText: response.statusText,
        body: errorBody.substring(0, 500)
      });
      throw new Error(`TomTom API error: ${response.status} - ${errorBody.substring(0, 200)}`);
    }
    console.log('🚗 Fetching traffic from TomTom with smart alternatives...');
    
    const data = await response.json();
    
const routes = [];
for (const [index, route] of data.routes.entries()) {
  const summary = route.summary;
  
  const normalTime = summary.lengthInMeters / 13.4;
  const actualTime = summary.travelTimeInSeconds;
  const delaySeconds = Math.max(0, actualTime - normalTime);
  
  const { waypoints, majorRoads } = extractRouteWaypoints(route);
  
  // ✅ Add delay between routes (except first one)
  if (index > 0) {
    console.log(`⏳ Waiting 500ms before processing route ${index + 1}...`);
    await new Promise(resolve => setTimeout(resolve, 500));
  }
  
  const waypointNames = await getWaypointNames(waypoints);
  
  let routeDescription = '';
  if (majorRoads.length > 0) {
    const roadPhrases = ['taking', 'using', 'via', 'going down', 'heading down'];
    const phrase = roadPhrases[Math.floor(Math.random() * roadPhrases.length)];
    routeDescription = `${phrase} ${majorRoads.join(' and ')}`;
  } else if (waypointNames.length > 0) {
    const cityPhrases = ['through', 'heading through', 'going through', 'passing through', 'via'];
    const phrase = cityPhrases[Math.floor(Math.random() * cityPhrases.length)];
    routeDescription = `${phrase} ${waypointNames.join(', ')}`;
  }
  
  routes.push({
    routeIndex: index,
    duration: Math.round(actualTime / 60),
    durationInTraffic: Math.round(actualTime / 60),
    delay: Math.round(delaySeconds / 60),
    distance: (summary.lengthInMeters / 1609.34).toFixed(1),
    trafficLevel: delaySeconds > 300 ? 'heavy' : delaySeconds > 120 ? 'moderate' : 'light',
    isPrimary: index === 0,
    routeDescription: routeDescription,
    majorRoads: majorRoads,
    waypointNames: waypointNames,
    routeType: index === 0 ? 'usual route' : 'alternate route'
  });
}
    const primaryRoute = routes[0];
    const alternatives = routes.slice(1);
    
    const bestAlternative = alternatives.reduce((best, current) => {
      if (!best) return current;
      
      const bestScore = best.duration + (parseFloat(best.distance) * 0.8);
      const currentScore = current.duration + (parseFloat(current.distance) * 0.8);
      
      return currentScore < bestScore ? current : best;
    }, null);
    
    let timeSavings = 0;
    let distanceDifference = 0;
    let recommendation = null;
    
    if (bestAlternative) {
      timeSavings = primaryRoute.duration - bestAlternative.duration;
      distanceDifference = parseFloat(bestAlternative.distance) - parseFloat(primaryRoute.distance);
      
      let routeDetails = '';
      if (bestAlternative.routeDescription) {
        const cleanDesc = bestAlternative.routeDescription
          .replace(/^(taking|using|on|going down|through|heading through|going through|passing through|via)\s+/i, '');
        
        const recommendationPhrases = {
          roads: ['by taking', 'if you take', 'by using', 'by heading down', 'via'],
          cities: ['by going through', 'by heading through', 'if you go through', 'by passing through', 'via']
        };
        
        const isRoads = bestAlternative.majorRoads && bestAlternative.majorRoads.length > 0;
        const phrases = isRoads ? recommendationPhrases.roads : recommendationPhrases.cities;
        const phrase = phrases[Math.floor(Math.random() * phrases.length)];
        
        routeDetails = ` ${phrase} ${cleanDesc}`;
      }
      
      if (timeSavings >= 5) {
        recommendation = {
          type: 'strong',
          message: `save ${timeSavings} minutes by taking the alternate route${routeDetails}`,
          reason: distanceDifference < 2 ? 'with similar distance' : `though it's ${Math.abs(distanceDifference).toFixed(1)} miles ${distanceDifference > 0 ? 'longer' : 'shorter'}`
        };
      } else if (timeSavings >= 2 && primaryRoute.trafficLevel === 'heavy') {
        recommendation = {
          type: 'moderate',
          message: `save ${timeSavings} minutes and avoid heavy traffic by going${routeDetails}`,
          reason: 'on your usual route'
        };
      } else if (timeSavings >= 2) {
        recommendation = {
          type: 'light',
          message: `save ${timeSavings} minutes by taking${routeDetails}`,
          reason: 'if you want to avoid the usual route'
        };
      }
    }
    
    return {
      success: true,
      provider: 'TomTom Enhanced',
      primaryRoute: primaryRoute,
      alternativeRoute: bestAlternative,
      timeSavings: timeSavings,
      distanceDifference: distanceDifference,
      hasGoodAlternative: bestAlternative && timeSavings >= 2,
      recommendation: recommendation,
      allRoutes: routes
    };
    
  } catch (error) {
    console.error('❌ TomTom Enhanced failed:', error.message);
    return null;
  }
};

/**
 * Analyze best departure time based on traffic patterns
 */
const analyzeDepartureTime = (trafficLevel, currentHour) => {
  const isRushHour = (currentHour >= 7 && currentHour <= 9) || (currentHour >= 16 && currentHour <= 19);
  
  if (trafficLevel === 'heavy' && isRushHour) {
    if (currentHour >= 7 && currentHour < 9) {
      return {
        suggestion: 'leave within 15 minutes or wait until after 9:30 AM',
        reason: 'morning rush hour peaks between 8-9 AM'
      };
    } else if (currentHour >= 16 && currentHour < 19) {
      return {
        suggestion: 'wait 30-45 minutes if possible',
        reason: 'evening rush hour peaks between 5-6 PM'
      };
    }
  } else if (trafficLevel === 'moderate' && isRushHour) {
    return {
      suggestion: 'leave soon before traffic gets worse',
      reason: 'conditions are manageable now but deteriorating'
    };
  } else if (trafficLevel === 'light') {
    return {
      suggestion: 'great time to go',
      reason: 'traffic is flowing smoothly'
    };
  }
  
  return null;
};

/**
 * Basic fallback for when enhanced API fails
 */
const getBasicTrafficFallback = async (origin, destination) => {
  try {
    const url = `https://api.tomtom.com/routing/1/calculateRoute/${origin}:${destination}/json?key=${TRAFFIC_CONFIG.TOMTOM_KEY}&traffic=true&travelMode=car`;
    
    const response = await fetch(url);
    if (!response.ok) throw new Error('Fallback failed');
    
    const data = await response.json();
    const summary = data.routes[0].summary;
    
    const normalTime = summary.lengthInMeters / 13.4;
    const actualTime = summary.travelTimeInSeconds;
    const delaySeconds = Math.max(0, actualTime - normalTime);
    
    return {
      success: true,
      provider: 'TomTom Basic',
      primaryRoute: {
        duration: Math.round(actualTime / 60),
        delay: Math.round(delaySeconds / 60),
        distance: (summary.lengthInMeters / 1609.34).toFixed(1),
        trafficLevel: delaySeconds > 300 ? 'heavy' : delaySeconds > 120 ? 'moderate' : 'light'
      },
      hasGoodAlternative: false
    };
  } catch (error) {
    console.error('Basic fallback failed:', error);
    return null;
  }
};

/**
 * Main enhanced traffic handler
 */
export const getSmartTrafficData = async (routeName = null) => {
  try {
    const savedRoutes = getSavedRoutes();
    
    let route;
    if (routeName) {
      route = savedRoutes.find(r => r.name.toLowerCase() === routeName.toLowerCase());
      if (!route) {
        console.warn(`⚠️ Route "${routeName}" not found`);
        return null;
      }
    } else {
      route = savedRoutes[0];
      if (!route) {
        console.warn('⚠️ No saved routes found');
        return null;
      }
    }
    
    console.log(`🚗 Getting smart traffic analysis for: ${route.name}`);
    
    const originCoords = route.originCoords || route.origin;
    const destCoords = route.destCoords || route.destination;
    
    let trafficData = await getTomTomTrafficWithAlternatives(originCoords, destCoords);
    
    if (!trafficData) {
      console.log('⚠️ Enhanced API failed, falling back to basic...');
      trafficData = await getBasicTrafficFallback(originCoords, destCoords);
    }
    
    if (trafficData) {
      const currentHour = new Date().getHours();
      const departureAdvice = analyzeDepartureTime(
        trafficData.primaryRoute.trafficLevel, 
        currentHour
      );
      
      console.log('✅ Smart traffic data retrieved:', trafficData);
      
      return {
        ...trafficData,
        routeName: route.name,
        origin: route.origin,
        destination: route.destination,
        departureAdvice: departureAdvice,
        currentTime: new Date().toLocaleTimeString('en-US', { 
          hour: 'numeric', 
          minute: '2-digit' 
        })
      };
    }
    
    return null;
    
  } catch (error) {
    console.error('❌ Smart traffic module error:', error);
    return null;
  }
};

/**
 * Format smart traffic data for podcast with natural locations
 */
export const formatSmartTrafficForPodcast = (trafficData) => {
  if (!trafficData) {
    return 'Traffic information is currently unavailable.';
  }
  
  const { 
    routeName, 
    primaryRoute, 
    alternativeRoute, 
    recommendation,
    departureAdvice
  } = trafficData;
  
  let content = `Looking at your commute for ${routeName}, `;
  
  const primaryRouteDesc = primaryRoute.routeDescription 
    ? ` ${primaryRoute.routeDescription}` 
    : '';
  
  if (primaryRoute.delay > 5) {
    content += `there's a ${primaryRoute.delay}-minute delay due to ${primaryRoute.trafficLevel} traffic${primaryRouteDesc}. `;
    content += `Your usual ${primaryRoute.distance}-mile route will take about ${primaryRoute.duration} minutes. `;
  } else if (primaryRoute.delay > 2) {
    content += `traffic is ${primaryRoute.trafficLevel}${primaryRouteDesc}. `;
    content += `Expect about ${primaryRoute.duration} minutes for your ${primaryRoute.distance}-mile drive. `;
  } else {
    content += `traffic is ${primaryRoute.trafficLevel}${primaryRouteDesc}! `;
    content += `Your ${primaryRoute.distance}-mile drive should take about ${primaryRoute.duration} minutes. `;
  }
  
  if (recommendation) {
    if (recommendation.type === 'strong') {
      content += `But here's a better option: you can ${recommendation.message} ${recommendation.reason}. `;
      content += `It's ${alternativeRoute.distance} miles and will only take ${alternativeRoute.duration} minutes. `;
      content += `Definitely worth considering. `;
    } else if (recommendation.type === 'moderate') {
      content += `There's an option to ${recommendation.message}. `;
      content += `It's ${alternativeRoute.distance} miles and takes about ${alternativeRoute.duration} minutes. `;
      content += `Might be worth the switch. `;
    } else if (recommendation.type === 'light') {
      content += `If you want variety, you could ${recommendation.message}. `;
      content += `Takes about ${alternativeRoute.duration} minutes. `;
    }
  }
  
  if (departureAdvice) {
    content += `Timing-wise, ${departureAdvice.suggestion} because ${departureAdvice.reason}. `;
  } else if (primaryRoute.trafficLevel === 'light') {
    content += `Perfect time to head out. `;
  }
  
  return content.trim();
};

/**
 * Reverse geocode coordinates to readable address
 */
export const reverseGeocode = async (coords) => {
  try {
    console.log('🔄 Reverse geocoding with TomTom:', coords);
    
    if (!TRAFFIC_CONFIG.TOMTOM_KEY || TRAFFIC_CONFIG.TOMTOM_KEY === 'ss') {
      console.error('❌ Invalid TomTom API key for reverse geocoding');
      return coords;
    }
    
    const [lat, lng] = coords.split(',').map(s => s.trim());
    
    if (!lat || !lng || isNaN(lat) || isNaN(lng)) {
      console.error('❌ Invalid coordinates:', coords);
      return coords;
    }
    
    const url = `https://api.tomtom.com/search/2/reverseGeocode/${lat},${lng}.json?key=${TRAFFIC_CONFIG.TOMTOM_KEY}`;
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);
    
    const response = await fetch(url, { signal: controller.signal });
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Could not read error');
      console.error('❌ TomTom API error:', response.status, errorText.substring(0, 200));
      return coords;
    }
    
    const data = await response.json();
    
    if (data.addresses && data.addresses.length > 0) {
      const address = data.addresses[0].address;
      
      const parts = [];
      if (address.streetNumber && address.streetName) {
        parts.push(`${address.streetNumber} ${address.streetName}`);
      } else if (address.streetName) {
        parts.push(address.streetName);
      }
      
      if (address.municipality) parts.push(address.municipality);
      if (address.countrySubdivision) parts.push(address.countrySubdivision);
      
      const formatted = parts.filter(p => p).join(', ');
      return formatted || coords;
    }
    
    return coords;
  } catch (error) {
    if (error.name === 'AbortError') {
      console.warn('⏱️ Reverse geocoding timeout - using coordinates');
    } else {
      console.error('❌ Reverse geocoding error:', error.message || error);
    }
    return coords;
  }
};

/**
 * Geocode address to coordinates
 */
export const geocodeAddress = async (address) => {
  try {
    console.log('🔍 Geocoding address with TomTom:', address);
    
    const url = `https://api.tomtom.com/search/2/geocode/${encodeURIComponent(address)}.json?key=${TRAFFIC_CONFIG.TOMTOM_KEY}&limit=1`;
    
    const response = await fetch(url);
    if (!response.ok) throw new Error('Geocoding failed');
    
    const data = await response.json();
    
    if (data.results && data.results.length > 0) {
      const position = data.results[0].position;
      const coords = `${position.lat},${position.lon}`;
      return coords;
    }
    
    return null;
  } catch (error) {
    console.error('❌ Geocoding error:', error);
    return null;
  }
};

/**
 * Get current location using browser geolocation
 */
export const getCurrentLocation = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation not supported'));
      return;
    }
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const coords = `${position.coords.latitude},${position.coords.longitude}`;
        resolve(coords);
      },
      (error) => reject(error)
    );
  });
};

/**
 * Get user's saved commute routes from localStorage
 */
export const getSavedRoutes = () => {
  try {
    const saved = localStorage.getItem('innerloop_commute_routes');
    return saved ? JSON.parse(saved) : [];
  } catch (error) {
    console.error('Error loading saved routes:', error);
    return [];
  }
};

/**
 * Save commute route to localStorage
 */
/**
 * Save route with automatic geocoding if needed
 */
export const saveRoute = async (routeName, origin, destination, originCoords, destinationCoords) => {
  try {
    console.log('💾 Saving route:', { routeName, origin, destination, originCoords, destinationCoords });
    
    // ✅ If coords aren't provided or look like addresses, geocode them
    let finalOriginCoords = originCoords;
    let finalDestCoords = destinationCoords;
    
    // Check if origin needs geocoding
    if (!finalOriginCoords || !finalOriginCoords.includes(',') || isNaN(parseFloat(finalOriginCoords.split(',')[0]))) {
      console.log('🔍 Origin needs geocoding:', origin);
      finalOriginCoords = await geocodeAddress(origin);
      
      if (!finalOriginCoords) {
        throw new Error(`Could not find coordinates for origin: ${origin}`);
      }
      console.log('✅ Origin geocoded to:', finalOriginCoords);
    }
    
    // Check if destination needs geocoding
    if (!finalDestCoords || !finalDestCoords.includes(',') || isNaN(parseFloat(finalDestCoords.split(',')[0]))) {
      console.log('🔍 Destination needs geocoding:', destination);
      finalDestCoords = await geocodeAddress(destination);
      
      if (!finalDestCoords) {
        throw new Error(`Could not find coordinates for destination: ${destination}`);
      }
      console.log('✅ Destination geocoded to:', finalDestCoords);
    }
    
    const routes = getSavedRoutes();
    const newRoute = {
      id: Date.now(),
      name: routeName,
      origin: origin, // Keep readable address
      destination: destination, // Keep readable address
      originCoords: finalOriginCoords, // Store coordinates
      destCoords: finalDestCoords, // Store coordinates
      createdAt: new Date().toISOString()
    };
    
    console.log('✅ Route ready to save:', newRoute);
    
    routes.push(newRoute);
    localStorage.setItem('innerloop_commute_routes', JSON.stringify(routes));
    
    return newRoute;} catch (error) {
    console.error('❌ Error saving route:', error);
    throw error; // Re-throw so UI can show error message
  }
};
/**
 * Validate route before saving
 */
export const validateRouteInputs = async (origin, destination) => {
  const errors = [];
  
  if (!origin || origin.trim() === '') {
    errors.push('Origin is required');
  }
  
  if (!destination || destination.trim() === '') {
    errors.push('Destination is required');
  }
  
  if (errors.length > 0) {
    return { valid: false, errors };
  }
  
  // Try to geocode both to verify they're valid
  try {
    console.log('🔍 Validating origin:', origin);
    const originCoords = await geocodeAddress(origin);
    
    if (!originCoords) {
      errors.push(`Could not find location: "${origin}". Please enter a valid address or city.`);
    }
    
    console.log('🔍 Validating destination:', destination);
    const destCoords = await geocodeAddress(destination);
    
    if (!destCoords) {
      errors.push(`Could not find location: "${destination}". Please enter a valid address or city.`);
    }
    
    if (errors.length > 0) {
      return { valid: false, errors };
    }
    
    return { 
      valid: true, 
      originCoords, 
      destCoords,
      message: 'Route is valid!'
    };
    
  } catch (error) {
    return { 
      valid: false, 
      errors: [`Validation error: ${error.message}`] 
    };
  }
};
/**
 * Fix broken routes by re-geocoding them
 * Run this once to repair all saved routes
 */
export const repairAllRoutes = async () => {
  const routes = getSavedRoutes();
  
  if (routes.length === 0) {
    console.log('No routes to repair');
    return { success: true, fixed: 0, failed: 0 };
  }
  
  console.log(`🔧 Repairing ${routes.length} route(s)...`);
  
  const repairedRoutes = [];
  let fixedCount = 0;
  let failedCount = 0;
  
  for (const route of routes) {
    try {
      console.log(`\n📍 Checking route: "${route.name}"`);
      
      let originCoords = route.originCoords || route.origin;
      let destCoords = route.destCoords || route.destination || route.dest;
      
      let needsRepair = false;
      
      // Check if origin needs fixing
      if (!originCoords || !originCoords.includes(',') || isNaN(parseFloat(originCoords.split(',')[0]))) {
        console.log('   🔧 Origin needs repair');
        originCoords = await geocodeAddress(route.origin);
        needsRepair = true;
        
        if (!originCoords) {
          console.error('   ❌ Could not repair origin');
          failedCount++;
          continue;
        }
      }
      
      // Check if destination needs fixing
      if (!destCoords || !destCoords.includes(',') || isNaN(parseFloat(destCoords.split(',')[0]))) {
        console.log('   🔧 Destination needs repair');
        destCoords = await geocodeAddress(route.destination);
        needsRepair = true;
        
        if (!destCoords) {
          console.error('   ❌ Could not repair destination');
          failedCount++;
          continue;
        }
      }
      
      if (needsRepair) {
        console.log('   ✅ Route repaired!');
        fixedCount++;
      } else {
        console.log('   ✅ Route was already valid');
      }
      
      repairedRoutes.push({
        ...route,
        originCoords: originCoords,
        destCoords: destCoords
      });
      
    } catch (error) {
      console.error(`   ❌ Failed to repair route "${route.name}":`, error.message);
      failedCount++;
    }
  }
  
  // Save repaired routes
  if (repairedRoutes.length > 0) {
    localStorage.setItem('innerloop_commute_routes', JSON.stringify(repairedRoutes));
    console.log(`\n✅ Saved ${repairedRoutes.length} repaired route(s)`);
  }
  
  console.log('\n📊 REPAIR SUMMARY:');
  console.log(`   Fixed: ${fixedCount}`);
  console.log(`   Failed: ${failedCount}`);
  console.log(`   Total: ${routes.length}`);
  
  return {
    success: failedCount === 0,
    fixed: fixedCount,
    failed: failedCount,
    total: routes.length
  };
};

// Make it available globally
if (typeof window !== 'undefined') {
  window.repairTrafficRoutes = repairAllRoutes;
}
/**
 * Delete a saved route
 */
export const deleteRoute = (routeId) => {
  try {
    const routes = getSavedRoutes();
    const filteredRoutes = routes.filter(r => r.id !== routeId);
    localStorage.setItem('innerloop_commute_routes', JSON.stringify(filteredRoutes));
    return true;
  } catch (error) {
    console.error('Error deleting route:', error);
    return false;
  }
};

/**
 * Backward compatibility aliases
 */
export const getTrafficData = getSmartTrafficData;
export const formatTrafficForPodcast = formatSmartTrafficForPodcast;

/**
 * Default export with all functions
 */
export default {
  getSmartTrafficData,
  formatSmartTrafficForPodcast,
  getTrafficData,
  formatTrafficForPodcast,
  getSavedRoutes,
  saveRoute,
  deleteRoute,
  getCurrentLocation,
  geocodeAddress,
  reverseGeocode
};